import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { logger } from "../../observability/LoggerFactory";
import { useState, useCallback } from "react";
import type { paths } from "../../api/generated/api-types";

type ParseRequest =
  paths["/sites/import/parse"]["post"]["requestBody"]["content"]["application/json"];

type ParseResponse =
  paths["/sites/import/parse"]["post"]["responses"]["200"]["content"]["application/json"];

type SaveRequest =
  paths["/sites/import/save"]["post"]["requestBody"]["content"]["application/json"];

type SaveResponse =
  paths["/sites/import/save"]["post"]["responses"]["200"]["content"]["application/json"];

export type ParsedEntry = ParseResponse["entries"][number];
export type ParseErrorRow = ParseResponse["parseErrors"][number];
export type ParseSummary = ParseResponse["summary"];

export interface FormFields {
  rawData: string;
  seller: string;
}

type SiteImportState =
  | { kind: "form" }
  | { kind: "parsing"; form: FormFields }
  | {
      kind: "results";
      entries: ParsedEntry[];
      parseErrors: ParseErrorRow[];
      summary: ParseSummary;
      form: FormFields;
    }
  | { kind: "saving" }
  | { kind: "success"; result: SaveResponse }
  | { kind: "error"; message: string };

export interface UseSiteImportResult {
  state: SiteImportState;
  submitParse: (form: FormFields) => Promise<void>;
  confirmSave: () => void;
  reset: () => void;
}

export function useSiteImport(): UseSiteImportResult {
  const [state, setState] = useState<SiteImportState>({ kind: "form" });

  const submitParse = useCallback(async (form: FormFields) => {
    setState({ kind: "parsing", form });

    try {
      const body: ParseRequest = {
        rawData: form.rawData,
        seller: form.seller,
      };

      const response = await fetchWithRefresh("/api/sites/import/parse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const submitParseOutcome = (await response.json()) as ParseResponse;
      setState({
        kind: "results",
        entries: submitParseOutcome.entries,
        parseErrors: submitParseOutcome.parseErrors,
        summary: submitParseOutcome.summary,
        form,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logger.error({ err: message, component: "useSiteImport" }, "Failed to parse imported sites");
      setState({ kind: "error", message });
    }
  }, []);

  const confirmSave = useCallback(() => {
    setState((previous) => {
      let next: SiteImportState = previous;
      if (previous.kind === "results") {
        const nonDuplicateEntries = previous.entries.filter((event) => !event.isDuplicate);

        void (async () => {
          try {
            const body: SaveRequest = {
              seller: previous.form.seller,
              entries: nonDuplicateEntries.map((event) => ({
                url: event.url,
                login: event.login,
                password: event.password,
                region: event.region,
                blogId: event.blogId,
                dr: event.dr,
                organicKeywords: event.organicKeywords,
                age: event.age,
              })),
            };

            const response = await fetchWithRefresh("/api/sites/import/save", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }

            const saved = (await response.json()) as SaveResponse;
            setState({ kind: "success", result: saved });
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            logger.error({ err: message, component: "useSiteImport" }, "Failed to save imported sites");
            setState({ kind: "error", message });
          }
        })();
        next = { kind: "saving" };
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setState({ kind: "form" });
  }, []);

  return { state, submitParse, confirmSave, reset };
}
